import { call, put, takeEvery } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import stockService, {
  CreateStockItemData,
  UpdateStockItemData,
  StockFilters,
  UpdateQuantityData,
} from "../../services/stockService";
import {
  getStockItemsStart,
  getStockItemsSuccess,
  getStockItemsFailure,
  getStockItemStart,
  getStockItemSuccess,
  getStockItemFailure,
  createStockItemStart,
  createStockItemSuccess,
  createStockItemFailure,
  updateStockItemStart,
  updateStockItemSuccess,
  updateStockItemFailure,
  updateStockQuantityStart,
  updateStockQuantitySuccess,
  updateStockQuantityFailure,
  deleteStockItemStart,
  deleteStockItemSuccess,
  deleteStockItemFailure,
  getStockAlertsStart,
  getStockAlertsSuccess,
  getStockAlertsFailure,
  getStockStatsStart,
  getStockStatsSuccess,
  getStockStatsFailure,
} from "../slices/stockSlice";

// Get stock items saga
function* getStockItemsSaga(
  action: PayloadAction<StockFilters>
): Generator<any, void, any> {
  try {
    console.log(
      "stockSaga: getStockItemsSaga called with payload:",
      action.payload
    );
    const response: any = yield call(
      stockService.getStockItems,
      action.payload
    );
    console.log(
      "stockSaga: getStockItemsSaga success, dispatching success action"
    );
    yield put(
      getStockItemsSuccess({
        data: response.data,
        pagination: response.pagination,
      })
    );
  } catch (error: any) {
    console.error("stockSaga: getStockItemsSaga error:", error);
    yield put(
      getStockItemsFailure(
        error.response?.data?.message || "Failed to fetch stock items"
      )
    );
  }
}

// Get single stock item saga
function* getStockItemSaga(
  action: PayloadAction<string>
): Generator<any, void, any> {
  try {
    const response: any = yield call(stockService.getStockItem, action.payload);
    yield put(getStockItemSuccess(response.data));
  } catch (error: any) {
    yield put(
      getStockItemFailure(
        error.response?.data?.message || "Failed to fetch stock item"
      )
    );
  }
}

// Create stock item saga
function* createStockItemSaga(
  action: PayloadAction<CreateStockItemData>
): Generator<any, void, any> {
  try {
    const response: any = yield call(
      stockService.createStockItem,
      action.payload
    );
    yield put(createStockItemSuccess(response.data));
  } catch (error: any) {
    yield put(
      createStockItemFailure(
        error.response?.data?.message || "Failed to create stock item"
      )
    );
  }
}

// Update stock item saga
function* updateStockItemSaga(
  action: PayloadAction<{
    id: string;
    data: UpdateStockItemData;
  }>
): Generator<any, void, any> {
  try {
    const response: any = yield call(
      stockService.updateStockItem,
      action.payload.id,
      action.payload.data
    );
    yield put(updateStockItemSuccess(response.data));
  } catch (error: any) {
    yield put(
      updateStockItemFailure(
        error.response?.data?.message || "Failed to update stock item"
      )
    );
  }
}

// Update stock quantity saga
function* updateStockQuantitySaga(
  action: PayloadAction<{
    id: string;
    quantity: number;
    operation: "add" | "subtract";
  }>
): Generator<any, void, any> {
  try {
    const response: any = yield call(
      stockService.updateStockQuantity,
      action.payload.id,
      {
        quantity: action.payload.quantity,
        operation: action.payload.operation,
      }
    );
    yield put(updateStockQuantitySuccess(response.data));
  } catch (error: any) {
    yield put(
      updateStockQuantityFailure(
        error.response?.data?.message || "Failed to update stock quantity"
      )
    );
  }
}

// Delete stock item saga
function* deleteStockItemSaga(
  action: PayloadAction<string>
): Generator<any, void, any> {
  try {
    yield call(stockService.deleteStockItem, action.payload);
    yield put(deleteStockItemSuccess(action.payload));
  } catch (error: any) {
    yield put(
      deleteStockItemFailure(
        error.response?.data?.message || "Failed to delete stock item"
      )
    );
  }
}

// Get stock alerts saga
function* getStockAlertsSaga(): Generator<any, void, any> {
  try {
    console.log("stockSaga: getStockAlertsSaga called");
    const response: any = yield call(stockService.getStockAlerts);
    console.log("stockSaga: getStockAlertsSaga success");
    yield put(getStockAlertsSuccess(response.data));
  } catch (error: any) {
    console.error("stockSaga: getStockAlertsSaga error:", error);
    yield put(
      getStockAlertsFailure(
        error.response?.data?.message || "Failed to fetch stock alerts"
      )
    );
  }
}

// Get stock stats saga
function* getStockStatsSaga(): Generator<any, void, any> {
  try {
    console.log("stockSaga: getStockStatsSaga called");
    const response: any = yield call(stockService.getStockStats);
    console.log("stockSaga: getStockStatsSaga success");
    yield put(getStockStatsSuccess(response.data));
  } catch (error: any) {
    console.error("stockSaga: getStockStatsSaga error:", error);
    yield put(
      getStockStatsFailure(
        error.response?.data?.message || "Failed to fetch stock statistics"
      )
    );
  }
}

// Watchers
export function* watchGetStockItemsSaga() {
  yield takeEvery(getStockItemsStart.type, getStockItemsSaga);
}

export function* watchGetStockItemSaga() {
  yield takeEvery(getStockItemStart.type, getStockItemSaga);
}

export function* watchCreateStockItemSaga() {
  yield takeEvery(createStockItemStart.type, createStockItemSaga);
}

export function* watchUpdateStockItemSaga() {
  yield takeEvery(updateStockItemStart.type, updateStockItemSaga);
}

export function* watchUpdateStockQuantitySaga() {
  yield takeEvery(updateStockQuantityStart.type, updateStockQuantitySaga);
}

export function* watchDeleteStockItemSaga() {
  yield takeEvery(deleteStockItemStart.type, deleteStockItemSaga);
}

export function* watchGetStockAlertsSaga() {
  yield takeEvery(getStockAlertsStart.type, getStockAlertsSaga);
}

export function* watchGetStockStatsSaga() {
  yield takeEvery(getStockStatsStart.type, getStockStatsSaga);
}
